import { useQuery } from '@tanstack/react-query';
import type { PortalOverviewDto } from '@cpm/shared';
import { Boxes, Package } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  statusTone,
} from '../../components/ui.js';

export function PortalDashboardPage() {
  const navigate = useNavigate();
  const overview = useQuery({
    queryKey: ['portal', 'overview'],
    queryFn: () => api.get<PortalOverviewDto>('/api/portal/overview'),
    refetchInterval: 60_000,
  });

  if (overview.isPending) return <Spinner label="Loading your dashboard…" />;
  if (overview.error) return <ErrorNote error={overview.error} />;
  const data = overview.data;

  return (
    <div>
      <PageHeader
        title={`Welcome, ${data.client.name}`}
        description={data.client.company ?? data.client.email}
      />

      <div className="mb-6 grid grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-4 p-5">
            <div className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
              <Package className="size-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{data.subscriptions.length}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Subscriptions</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4 p-5">
            <div className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300">
              <Boxes className="size-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{data.resourceCount}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Resources</p>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Your subscriptions"
          actions={
            <Link to="/portal/subscriptions" className="text-sm text-brand-600 hover:underline">
              View all
            </Link>
          }
        />
        {data.subscriptions.length === 0 ? (
          <EmptyState
            icon={<Package className="size-10" />}
            title="No subscriptions yet"
            description="Your hosting provider has not set up a subscription for you yet."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Subscription</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Health</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.subscriptions.map((sub) => (
                  <tr
                    key={sub.id}
                    className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => void navigate(`/portal/subscriptions/${sub.id}`)}
                  >
                    <td className="px-5 py-3 font-medium">{sub.name}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{sub.planName}</td>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(sub.health)}>{sub.health}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <span className="flex flex-wrap gap-1.5">
                        <Badge tone={statusTone(sub.status)}>{sub.status}</Badge>
                        {sub.overQuota && <Badge tone="red">over quota</Badge>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
